<script setup lang="ts">
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { theme } from '#tailwind-config'

const props = defineProps<{
  data: SectionAwardsListRecord
  moduleIndex: number
}>()

const { isSemantic } = useSectionRules(props.moduleIndex)

const breakpoints = useBreakpoints(theme.screens)
const isScreenLg = breakpoints.greater('lg')

const ctxRef = ref<HTMLElement | null>(null)
let ctx: gsap.Context | null = null

onMounted(() => {
  nextTick(() => {
    if (ctxRef.value) {
      gsap.registerPlugin(ScrollTrigger)
      const mm = gsap.matchMedia()
      mm.add('(min-width: 1024px)', () => {
        ctx = gsap.context(() => {
          const items = gsap.utils.toArray('.fx-a-lt')
          if (!items.length) return

          const tl = useHorizontalLoop(items, {
            paused: true,
            speed: 1,
            repeat: -1
          })

          ScrollTrigger.create({
            trigger: ctxRef.value,
            start: 'top bottom',
            markers: false,
            onEnter: () => {
              tl?.play()
            },
            onLeave: () => {
              tl?.pause()
            },
            onEnterBack: () => {
              tl?.play()
            },
            onLeaveBack: () => {
              tl?.pause()
            }
          })
          ScrollTrigger.refresh()
        }, ctxRef.value as HTMLElement)
      })
    }
  })
})

onUnmounted(() => {
  if (ctx) {
    ctx.revert()
  }
})
</script>

<template>
  <div
    ref="ctxRef"
    class="container flex min-h-[75rem] w-full flex-col justify-center overflow-hidden bg-splash bg-cover bg-[50%_50%] bg-no-repeat py-20 lg:min-h-[32rem]"
  >
    <Heading
      class="absolute inset-0 flex transform-gpu flex-col items-center justify-center text-center text-[11.25rem] font-black leading-[0.84] tracking-tighter text-white lg:flex-row lg:clamp-[15rem,25vw,25rem]"
      :level="2"
      :size="undefined"
      :is-semantic="isSemantic"
    >
      <span class="fx-a-lt">A</span>
      <span class="fx-a-lt">W</span>
      <span class="fx-a-lt">A</span>
      <span class="fx-a-lt">R</span>
      <span class="fx-a-lt">D</span>
      <span class="fx-a-lt">S</span>
      <span class="fx-a-lt hidden lg:inline-block">A</span>
      <span class="fx-a-lt hidden lg:inline-block">W</span>
      <span class="fx-a-lt hidden lg:inline-block">A</span>
      <span class="fx-a-lt hidden lg:inline-block">R</span>
      <span class="fx-a-lt hidden lg:inline-block">D</span>
      <span class="fx-a-lt hidden lg:inline-block">S</span>
    </Heading>
    <div class="relative flex flex-col items-center justify-center gap-10 lg:flex-row lg:gap-16">
      <div
        v-for="(item, idx) in data.blocks"
        :key="idx"
        class="flex flex-1 flex-col items-center gap-4 text-center lg:max-w-[25%]"
      >
        <div v-if="item.media" class="aspect-square">
          <ClientOnly>
            <DiamondGlass :size="isScreenLg ? 'lg' : 'md'">
              <Image
                loading="lazy"
                class="aspect-square h-auto w-20 object-contain lg:w-36"
                sizes="auto"
                :src="item.media.responsiveImage ? item.media.responsiveImage?.src : item.media.url"
                :srcset="
                  item.media.responsiveImage ? item.media.responsiveImage?.webpSrcSet : undefined
                "
                :width="
                  item.media.responsiveImage
                    ? item.media.responsiveImage?.width
                    : (item.media.width ?? 150)
                "
                :height="
                  item.media.responsiveImage
                    ? item.media.responsiveImage?.height
                    : (item.media.height ?? 150)
                "
                :alt="item.media.alt ?? ''"
              />
            </DiamondGlass>
          </ClientOnly>
        </div>
        <div v-if="item.name" class="font-black text-black">{{ item.name }}</div>
      </div>
    </div>
  </div>
</template>
